/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { ExpandMore } from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    MenuItem,
    Select,
    TextField,
    Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';

/*  Helpers */
import { Notify } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import dayjs from 'dayjs';

/*  React  */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const getTrialEndDate = (settingsFormData, originalTrialEndDate) => {
    if (settingsFormData.hubType === 'Premium') {
        return null;
    }

    const today = new Date();
    // Let's add 14 days, just like the trial length. 15th day at 00:00:00.
    const todayPlus14 = new Date(
        new Date(today.setDate(today.getDate() + 15)).setUTCHours(0, 0, 0, 0)
    );

    return settingsFormData.trialEndDate || originalTrialEndDate || todayPlus14;
};

const saveSettings = async (tenant, tenantSettingsData, t) => {
    // Note: If we add more Tenant Settings
    // We'll most likely loop through the settings and save them individually
    try {
        // These settings are only applicable to a partner tenant
        if (tenant.partner) {
            await axios.post('/tenantSetting', {
                global: true,
                key: 'noSaaslioBrand',
                platformTenantId: tenant.id,
                value: tenantSettingsData.noSaaslioBrand
            });

            const trialEndDate = getTrialEndDate(
                tenantSettingsData,
                tenant.originalTrialEndDate
            );
            await axios.post('/tenantSetting', {
                global: true,
                key: 'trialEndDate',
                platformTenantId: tenant.id,
                value: trialEndDate
            });

            await axios.post('/tenantSetting', {
                global: true,
                key: 'hubType',
                platformTenantId: tenant.id,
                value: tenantSettingsData.hubType
            });

            await axios.post('/tenantSetting', {
                global: true,
                key: 'agentHandleExtensionActivity',
                platformTenantId: tenant.id,
                value: tenantSettingsData.agentHandleExtensionActivity
            });
        }

        return true;
    } catch (e) {
        Notify.e(
            t('GlobalAdmin.Tenant.Edit.Actions.unabletoSaveTenantSettings')
        );
        debug.e(e);
        return false;
    }
};

const save = async (
    tenant,
    tenantFormData,
    settingsFormData,
    onSuccessFn,
    t
) => {
    const { blocked, name } = tenantFormData;

    try {
        await axios.put(`/tenant/${tenant.id}`, {
            blocked,
            global: true,
            name
        });
    } catch (e) {
        Notify.e(e.toString());
        return;
    }

    const success = await saveSettings(tenant, settingsFormData, t);

    if (success) {
        Notify.s(t('GlobalAdmin.Tenant.Edit.Actions.successfullySavedTenant'));
        onSuccessFn();
    }
};

export const TenantActions = ({ tenant, onSuccessFn }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(true);

    const { settings: originalSettings, adminUser } = tenant;

    const [tenantFormData, setTenantFormData] = useState({
        blocked: tenant.blocked,
        name: tenant.name
    });

    const [settingsFormData, setSettingsFormData] = useState({
        adminUser,
        agentHandleExtensionActivity:
            originalSettings?.agentHandleExtensionActivity || false,
        hubType: originalSettings?.hubType || 'None',
        noSaaslioBrand: originalSettings?.noSaaslioBrand || false,
        trialEndDate: originalSettings?.trialEndDate || null
    });

    const datePickerDisabled = settingsFormData.hubType === 'Premium';

    // I think this is supposed to be togglable in state.
    const { partner } = tenant;

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            slotProps={{ transition: { unmountOnExit: true } }}
        >
            <AccordionSummary
                expandIcon={
                    <IconWithVariant
                        data-testid="expandAccordionIconTenantActions"
                        variant="primary"
                        src="ChevronDown"
                        size={FontSizes[2]}
                    />
                }
            >
                <Box display="flex" alignItems="center">
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {t('GlobalAdmin.Tenant.Edit.Actions.title')}
                    </Typography>
                </Box>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <Typography color="textSecondary" fontWeight={600}>
                            {t('Common.name')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} style={{ marginBottom: '20px' }}>
                        <TextField
                            onChange={(e) =>
                                setTenantFormData({
                                    ...tenantFormData,
                                    name: e.target.value
                                })
                            }
                            value={tenantFormData.name}
                            fullWidth
                        />
                    </Grid>
                    {partner && (
                        <Grid item xs={12}>
                            <Typography
                                color="textSecondary"
                                style={{ fontWeight: 600 }}
                            >
                                {t(
                                    'GlobalAdmin.Tenant.Edit.Actions.PartnerSettings'
                                )}
                            </Typography>
                            <Grid item xs={12} mb="16px">
                                <Box display="flex" alignItems="baseline">
                                    <FormLabel
                                        style={{
                                            fontSize: FontSizes[1],
                                            marginRight: '8px'
                                        }}
                                    >
                                        {t(
                                            'GlobalAdmin.Tenant.TrialEndDate.label'
                                        )}
                                        :
                                    </FormLabel>
                                    <DatePicker
                                        disabled={datePickerDisabled}
                                        value={
                                            settingsFormData.trialEndDate &&
                                            settingsFormData.hubType !==
                                                'Premium'
                                                ? dayjs(
                                                      settingsFormData.trialEndDate
                                                  )
                                                : null
                                        }
                                        onChange={(selectedDate) => {
                                            setSettingsFormData({
                                                ...settingsFormData,
                                                trialEndDate:
                                                    selectedDate &&
                                                    settingsFormData.hubType !==
                                                        'Premium'
                                                        ? selectedDate
                                                        : null
                                            });
                                        }}
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} mb="16px">
                                <FormLabel
                                    style={{
                                        display: 'block',
                                        fontWeight: 600,
                                        marginBottom: '4px'
                                    }}
                                >
                                    <Typography
                                        color="textSecondary"
                                        style={{ fontWeight: 600 }}
                                    >
                                        {t(
                                            'GlobalAdmin.Tenant.Edit.Actions.hubType.label'
                                        )}
                                    </Typography>
                                </FormLabel>
                                <FormControl
                                    variant="outlined"
                                    style={{ width: '100%' }}
                                >
                                    <Select
                                        IconComponent={ExpandMore}
                                        variant="standard"
                                        value={settingsFormData.hubType}
                                        onChange={(e) =>
                                            setSettingsFormData({
                                                ...settingsFormData,
                                                datePickerDisabled:
                                                    e.target.value ===
                                                    'Premium',
                                                hubType: e.target.value,
                                                trialEndDate:
                                                    e.target.value === 'Premium'
                                                        ? null
                                                        : originalSettings.trialEndDate
                                            })
                                        }
                                    >
                                        <MenuItem value="None">
                                            {t(
                                                'GlobalAdmin.Tenant.Edit.Actions.hubType.none'
                                            )}
                                        </MenuItem>
                                        <MenuItem value="Premium">
                                            {t(
                                                'GlobalAdmin.Tenant.Edit.Actions.hubType.premium'
                                            )}
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={tenantFormData.blocked}
                                    onChange={(e) => {
                                        setTenantFormData({
                                            ...tenantFormData,
                                            blocked: e.target.checked
                                        });
                                    }}
                                />
                            }
                            label={t('Common.activity_blocked')}
                        />
                    </Grid>

                    {partner && (
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            settingsFormData.noSaaslioBrand
                                        }
                                        onChange={(e) =>
                                            setSettingsFormData({
                                                ...settingsFormData,
                                                noSaaslioBrand: e.target.checked
                                            })
                                        }
                                    />
                                }
                                label={t(
                                    'GlobalAdmin.Tenant.Edit.Actions.removePoweredByBranding'
                                )}
                            />
                        </Grid>
                    )}

                    {partner && (
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={
                                            settingsFormData.agentHandleExtensionActivity
                                        }
                                        onChange={(e) =>
                                            setSettingsFormData({
                                                ...settingsFormData,
                                                agentHandleExtensionActivity:
                                                    e.target.checked
                                            })
                                        }
                                    />
                                }
                                label={t(
                                    'GlobalAdmin.Tenant.Edit.Actions.enableAgentHandlingExtensionActivity'
                                )}
                            />
                        </Grid>
                    )}
                </Grid>

                <Grid item xs={12}>
                    <hr />
                </Grid>

                <Button
                    onClick={() =>
                        save(
                            tenant,
                            tenantFormData,
                            settingsFormData,
                            onSuccessFn,
                            t
                        )
                    }
                    color="primary"
                    variant="contained"
                >
                    {t('GlobalAdmin.Tenant.Edit.Actions.submitButton')}
                </Button>
            </AccordionDetails>
        </Accordion>
    );
};
